/**
 * Seeded runs (BULLET-HELL.md §D8) — the run-shape PRNG.
 *
 * Before this, every roll in the game was a bare `Math.random()`, so the
 * leaderboard compared runs that faced different waves, different cards and
 * different level-up offers. A seed gives the board a comparison frame it
 * simply did not have.
 *
 * Three decisions here are load-bearing, and each one rules something out:
 *
 * - **A seed fixes the DECK, not the frames.** This is deliberately not a
 *   deterministic replay: the simulation is driven by player input on a fixed
 *   timestep, and nothing about a shared seed makes two players dodge
 *   identically. What a seed guarantees is that two players are handed the same
 *   *content* — the same wave vocabulary per stage, the same card order out of
 *   each boss, the same level-up and Spoils offers in the same sequence. That
 *   is the whole of what "fair comparison" needs, and chasing lockstep
 *   determinism would have meant seeding several hundred cosmetic
 *   `Math.random()` calls (particle jitter, spawn positions, music variation)
 *   whose call counts depend on how the player is playing anyway.
 *
 * - **Streams are per domain, never one shared sequence.** A single run-wide
 *   generator would couple every domain to every other one's *call order*: a
 *   boss picks its next card on a timer, so a player who kills a wave two
 *   seconds slower would shift the shared cursor and be offered different
 *   level-up cards for the rest of the run — the exact unfairness a seed is
 *   supposed to remove. Each domain draws from its own generator, derived from
 *   `seed:name`, so drift inside one (`waves`, whose draws depend on how many
 *   bodies the player let spawn in ramp mode) cannot leak into another.
 *
 * - **An unseeded run gets `Math.random` back, untouched.** `stream()` returns
 *   `Math.random` itself when there is no seed, so a normal run is not quietly
 *   pinned to one deterministic sequence per boot, and every call site keeps
 *   `Math.random` as its default argument. Seeding is additive: no seed, no
 *   behaviour change anywhere.
 *
 * Algorithm: xmur3 to turn a seed string into 32 bits, mulberry32 to expand it.
 * Both are a few lines, dependency-free and stable across engines — which
 * matters more here than statistical quality, since a daily seed is worthless
 * if two browsers disagree about what it contains. `crypto.getRandomValues` and
 * friends are the wrong tool for exactly that reason: unreproducible.
 */

/** Hashes a seed string to a 32-bit unsigned integer (xmur3, one output). */
export function hashSeed(text) {
  const str = String(text);
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * One independent generator: a `() => number` in [0, 1), drop-in compatible
 * with `Math.random` so every consumer takes it as a plain function and never
 * learns it is holding a seeded stream.
 */
export function createStream(seed) {
  let state = (typeof seed === 'number' ? seed : hashSeed(seed)) >>> 0;
  return function next() {
    state = (state + 0x6D2B79F5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The per-run generator set. `stream(name)` is memoised, so a domain that asks
 * twice gets the same cursor rather than restarting its sequence — Game asks
 * once per domain at construction and hands the functions down, but a system
 * rebuilt mid-run (MinionWaveSystem, one per stage) must not silently rewind
 * the wave stream and replay the first stage's mix for the rest of the run.
 *
 * @param {string|null} seed - normalized seed (see data/seeds.js), or null for
 *   an ordinary unseeded run.
 */
export function createRunRng(seed = null) {
  if (!seed) {
    return { seed: null, stream: () => Math.random };
  }
  const streams = new Map();
  return {
    seed,
    stream(name) {
      let stream = streams.get(name);
      if (!stream) {
        stream = createStream(`${seed}:${name}`);
        streams.set(name, stream);
      }
      return stream;
    },
  };
}
